import { BrowserRouter, Navigate, Routes, Route, useLocation } from "react-router-dom";
import ScrollReset from "./components/layout/ScrollReset";
import SalesTourHost from "@tour/SalesTourHost.jsx";
import { withFilmQuery } from "@tour/film.js";
import { hasAgentAccess, hasBrokerAccess, hasClientAccess } from "./js/auth/authService";
import { useAuth } from "./js/hooks/useAuth";
import { useFilmDesk } from "./js/hooks/useFilmDesk";
import StartScreen from "./pages/StartScreen";
import BuyerStart from "./pages/buyer/BuyerStart";
import SellerWelcome from "./pages/seller/SellerWelcome";
import SellerHome from "./pages/seller/SellerHome";
import SellerPropertyDetails from "./pages/seller/SellerPropertyDetails";
import SellerInteriorFeatures from "./pages/seller/SellerInteriorFeatures";
import SellerExteriorFeatures from "./pages/seller/SellerExteriorFeatures";
import SellerUpgrades from "./pages/seller/SellerUpgrades";
import SellerConditionNotes from "./pages/seller/SellerConditionNotes";
import SellerPhotos from "./pages/seller/SellerPhotos";
import SellerImprovements from "./pages/seller/SellerImprovements";
import SellerPortfolioSummary from "./pages/seller/SellerPortfolioSummary";
import BuyerLocation from "./pages/buyer/BuyerLocation";
import BuyerHomeType from "./pages/buyer/BuyerHomeType";
import BuyerLot from "./pages/buyer/BuyerLot";
import BuyerExterior from "./pages/buyer/BuyerExterior";
import BuyerInterior from "./pages/buyer/BuyerInterior";
import BuyerLifestyle from "./pages/buyer/BuyerLifestyle";
import BuyerAccessibility from "./pages/buyer/BuyerAccessibility";
import BuyerComplete from "./pages/buyer/BuyerComplete";
import AgentLogin from "./pages/agent/AgentLogin";
import AgentDashboard from "./pages/agent/AgentDashboard";
import AgentClients from "./pages/agent/AgentClients";
import AgentClientDetail from "./pages/agent/AgentClientDetail";
import AgentFiles from "./pages/agent/AgentFiles";
import AgentFileDetail from "./pages/agent/AgentFileDetail";
import AgentDocuments from "./pages/agent/AgentDocuments";
import AgentCalendar from "./pages/agent/AgentCalendar";
import AgentMessages from "./pages/agent/AgentMessages";
import AgentResponseHub from "./pages/agent/response/AgentResponseHub";
import AgentResponseNew from "./pages/agent/response/AgentResponseNew";
import AgentResponseLocation from "./pages/agent/response/AgentResponseLocation";
import AgentResponseExterior from "./pages/agent/response/AgentResponseExterior";
import AgentResponseInterior from "./pages/agent/response/AgentResponseInterior";
import AgentResponseSystems from "./pages/agent/response/AgentResponseSystems";
import AgentResponseCondition from "./pages/agent/response/AgentResponseCondition";
import AgentResponseSummary from "./pages/agent/response/AgentResponseSummary";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import ChecklistOverview from "./pages/checklist/ChecklistOverview";
import MainDashboard from "./pages/dashboard/MainDashboard";
import InstallPage from "./pages/install/InstallPage";
import BrokerLogin from "./pages/broker/BrokerLogin";
import BrokerMessages from "./pages/broker/BrokerMessages";
import BrokerDashboard from "./pages/broker/BrokerDashboard";
import BrokerAudit from "./pages/broker/BrokerAudit";
import AgentAudit from "./pages/agent/AgentAudit";
import SellerLogin from "./pages/auth/SellerLogin";
import ClientMessages from "./pages/client/ClientMessages";
import AboutPage, { ContactPage, DisclaimerPage, EulaPage, PrivacyPage, TermsPage } from "./pages/legal/LegalPages";
import SystemNoticesPage from "./pages/legal/SystemNoticesPage";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminSuggestions from "./pages/admin/AdminSuggestions";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminControls from "./pages/admin/AdminControls";
import AdminSitemap from "./pages/admin/AdminSitemap";
import AdminIntegrity from "./pages/admin/AdminIntegrity";
import AdminBeta from "./pages/admin/AdminBeta";
import StartHereGuide from "./pages/desk/StartHereGuide";
import TheHubPage from "./pages/desk/TheHubPage";
import AdminTheHub from "./pages/admin/AdminTheHub";
import FoundersView from "./pages/admin/FoundersView";
import AccountManagement from "./pages/admin/AccountManagement";
import ProspectsPage from "./pages/desk/ProspectsPage";
import ProspectPortal from "./pages/public/ProspectPortal";
import ManualRedirect from "./pages/manuals/ManualRedirect";
import BetaHandoffHost from "./components/beta/BetaHandoffHost";
import AccountPortal from "./pages/account/AccountPortal";
import RestoreDesk from "./pages/restore/RestoreDesk";
import BackupDesk from "./pages/backup/BackupDesk";
import ClientPortalPage from "./pages/client/ClientPortalPage";
import PortalAccess from "./pages/client/PortalAccess";
import ClientAccessPage from "./pages/client/ClientAccessPage";
import ClientRegister from "./pages/client/ClientRegister";
import ClientSetup from "./pages/client/ClientSetup";
import ClientCodeRedirect from "./pages/client/ClientCodeRedirect";
import BrokerAgentControls from "./pages/broker/BrokerAgentControls";
import DeskClientLookup from "./pages/desk/DeskClientLookup";
import RequireLifecycleActive from "./components/auth/RequireLifecycleActive";
import RequirePackageDesk from "./components/auth/RequirePackageDesk";
import RequireRoleDesk from "./components/auth/RequireRoleDesk";
import RequireFeature from "./components/auth/RequireFeature";
import OnboardingGate from "./components/auth/OnboardingGate";
import VerifyEmailPending from "./pages/auth/VerifyEmailPending";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ForgotPassword from "./pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import RotatePassword from "./pages/onboarding/RotatePassword";
import SubscribeActivate from "./pages/onboarding/SubscribeActivate";
import ProfessionalSetup from "./pages/onboarding/ProfessionalSetup";

function FilmNavigate({ to }) {
  const { search } = useLocation();
  return <Navigate to={withFilmQuery(to, search)} replace />;
}

function RequireAgent({ children }) {
  const { role } = useAuth();
  const { filmOn, allowed, pending } = useFilmDesk("agent");
  if (hasAgentAccess(role) || allowed) {
    return (
      <RequireLifecycleActive>
        <RequirePackageDesk desk="agent">{children}</RequirePackageDesk>
      </RequireLifecycleActive>
    );
  }
  if (filmOn && pending) return null;
  if (filmOn) return <FilmNavigate to="/agent/login" />;
  if (hasBrokerAccess(role)) return <FilmNavigate to="/broker/dashboard" />;
  return <FilmNavigate to="/agent/login" />;
}

function RequireAdmin({ children }) {
  const { role } = useAuth();
  if (role === "admin") return children;
  return <FilmNavigate to="/" />;
}

function RequireBroker({ children }) {
  const { role } = useAuth();
  const { filmOn, allowed, pending } = useFilmDesk("broker");
  if (hasBrokerAccess(role) || role === "corporate" || allowed) {
    return (
      <RequireLifecycleActive>
        <RequirePackageDesk desk="broker">{children}</RequirePackageDesk>
      </RequireLifecycleActive>
    );
  }
  if (filmOn && pending) return null;
  if (filmOn) return <FilmNavigate to="/broker/login" />;
  if (hasAgentAccess(role)) return <FilmNavigate to="/agent/dashboard" />;
  return <FilmNavigate to="/broker/login" />;
}

function RequireProfessional({ children }) {
  const { role } = useAuth();
  if (role === "admin" || hasAgentAccess(role) || hasBrokerAccess(role)) {
    return <RequireLifecycleActive>{children}</RequireLifecycleActive>;
  }
  return <FilmNavigate to="/agent/login" />;
}

function DeskClientsHome() {
  const { role } = useAuth();
  if (hasBrokerAccess(role)) return <FilmNavigate to="/broker/clients" />;
  return <FilmNavigate to="/agent/clients" />;
}

function RequireClient({ children }) {
  const { role } = useAuth();
  if (role === "admin") return <FilmNavigate to="/admin/messages" />;
  if (hasClientAccess(role)) return children;
  if (hasBrokerAccess(role)) return <FilmNavigate to="/broker/dashboard" />;
  if (hasAgentAccess(role)) return <FilmNavigate to="/agent/messages" />;
  return <FilmNavigate to="/login" />;
}

function RequireBuyer({ children }) {
  return <RequireRoleDesk desk="buyer">{children}</RequireRoleDesk>;
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <OnboardingGate>
        <SalesTourHost />
        <ScrollReset />
        <BetaHandoffHost />
        <Routes>
        <Route path="/" element={<StartScreen />} />
        <Route path="/dashboard" element={<MainDashboard />} />
        <Route
          path="/checklist"
          element={
            <RequireBuyer>
              <ChecklistOverview />
            </RequireBuyer>
          }
        />
        <Route path="/login" element={<Login />} />
        <Route path="/restore" element={<RestoreDesk />} />
        <Route path="/backup" element={<BackupDesk />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/client" element={<ClientAccessPage />} />
        <Route path="/portal/access" element={<PortalAccess />} />
        <Route path="/buyer/:clientCode" element={<ClientPortalPage kind="buyer" />} />
        <Route path="/portal/buyer/:clientCode" element={<ClientPortalPage kind="buyer" />} />
        <Route path="/portal/seller/:clientCode" element={<ClientPortalPage kind="seller" />} />
        <Route path="/portal/account" element={<FilmNavigate to="/account" />} />
        <Route
          path="/account"
          element={
            <RequireProfessional>
              <AccountPortal />
            </RequireProfessional>
          }
        />
        <Route path="/account/lifecycle" element={<FilmNavigate to="/account?tab=lifecycle" />} />
        <Route path="/account/lifecycle/suspend" element={<FilmNavigate to="/account?tab=lifecycle&card=suspend" />} />
        <Route path="/account/lifecycle/cancel" element={<FilmNavigate to="/account?tab=lifecycle&card=cancel" />} />
        <Route path="/account/lifecycle/reactivate" element={<FilmNavigate to="/account?tab=lifecycle&card=reactivate" />} />
        <Route path="/account/lifecycle/compliance" element={<FilmNavigate to="/account?tab=lifecycle&card=status" />} />
        <Route
          path="/client/register"
          element={
            <RequireProfessional>
              <ClientRegister />
            </RequireProfessional>
          }
        />
        <Route path="/invite/:token" element={<ClientSetup />} />
        <Route path="/client/:clientCode" element={<ClientCodeRedirect />} />
        <Route
          path="/desk/clients"
          element={
            <RequireProfessional>
              <DeskClientsHome />
            </RequireProfessional>
          }
        />
        <Route
          path="/desk/clients/:clientCode"
          element={
            <RequireProfessional>
              <DeskClientLookup />
            </RequireProfessional>
          }
        />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/eula" element={<EulaPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/notices" element={<SystemNoticesPage />} />
        <Route path="/connect/:deskId" element={<ProspectPortal />} />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminDashboard />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/messages"
          element={
            <RequireAdmin>
              <AdminMessages />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/messages/archive"
          element={
            <RequireAdmin>
              <AdminMessages />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/suggestions"
          element={
            <RequireAdmin>
              <AdminSuggestions />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/beta"
          element={
            <RequireAdmin>
              <AdminBeta />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/hub"
          element={
            <RequireAdmin>
              <AdminTheHub />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/hub/:category"
          element={
            <RequireAdmin>
              <TheHubPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/founders"
          element={
            <RequireAdmin>
              <FoundersView />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/accounts"
          element={
            <RequireAdmin>
              <AccountManagement />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/accounts/:id"
          element={
            <RequireAdmin>
              <AccountManagement />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/prospects"
          element={
            <RequireAdmin>
              <ProspectsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/prospects/:id"
          element={
            <RequireAdmin>
              <ProspectsPage />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireAdmin>
              <AdminUsers />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/controls"
          element={
            <RequireAdmin>
              <AdminControls />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/sitemap"
          element={
            <RequireAdmin>
              <AdminSitemap />
            </RequireAdmin>
          }
        />
        <Route
          path="/admin/integrity"
          element={
            <RequireAdmin>
              <AdminIntegrity />
            </RequireAdmin>
          }
        />
        <Route path="/seller/login" element={<SellerLogin />} />
        <Route path="/broker/login" element={<BrokerLogin />} />
        <Route
          path="/broker/dashboard"
          element={
            <RequireBroker>
              <BrokerDashboard />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/start-here"
          element={
            <RequireBroker>
              <StartHereGuide />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/messages"
          element={
            <RequireBroker>
              <BrokerMessages />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/messages/archive"
          element={
            <RequireBroker>
              <RequireFeature feature="archive">
                <BrokerMessages />
              </RequireFeature>
            </RequireBroker>
          }
        />
        <Route
          path="/broker/office"
          element={
            <RequireBroker>
              <BrokerMessages />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/office/archive"
          element={
            <RequireBroker>
              <BrokerMessages />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/controls/agents"
          element={
            <RequireBroker>
              <RequireFeature feature="agent_controls">
                <BrokerAgentControls />
              </RequireFeature>
            </RequireBroker>
          }
        />
        <Route
          path="/broker/audit"
          element={
            <RequireBroker>
              <RequireFeature feature="retention">
                <BrokerAudit />
              </RequireFeature>
            </RequireBroker>
          }
        />
        <Route
          path="/broker/profile"
          element={<FilmNavigate to="/account" />}
        />
        <Route
          path="/broker/settings"
          element={<FilmNavigate to="/account?tab=privacy" />}
        />
        <Route
          path="/broker/prospects"
          element={
            <RequireBroker>
              <ProspectsPage />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/prospects/:id"
          element={
            <RequireBroker>
              <ProspectsPage />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/clients"
          element={
            <RequireBroker>
              <AgentClients />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/client/:id"
          element={
            <RequireBroker>
              <AgentClientDetail />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/files"
          element={
            <RequireBroker>
              <AgentFiles />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/files/:id"
          element={
            <RequireBroker>
              <AgentFileDetail />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/documents"
          element={
            <RequireBroker>
              <AgentDocuments />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/calendar"
          element={
            <RequireBroker>
              <AgentCalendar />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/hub"
          element={
            <RequireBroker>
              <Navigate to="/broker/hub/hall" replace />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/hub/:category"
          element={
            <RequireBroker>
              <TheHubPage />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses"
          element={
            <RequireBroker>
              <AgentResponseHub />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/new"
          element={
            <RequireBroker>
              <AgentResponseNew />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/location"
          element={
            <RequireBroker>
              <AgentResponseLocation />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/exterior"
          element={
            <RequireBroker>
              <AgentResponseExterior />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/interior"
          element={
            <RequireBroker>
              <AgentResponseInterior />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/systems"
          element={
            <RequireBroker>
              <AgentResponseSystems />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/condition"
          element={
            <RequireBroker>
              <AgentResponseCondition />
            </RequireBroker>
          }
        />
        <Route
          path="/broker/responses/:id/summary"
          element={
            <RequireBroker>
              <AgentResponseSummary />
            </RequireBroker>
          }
        />
        <Route
          path="/client/messages"
          element={
            <RequireClient>
              <ClientMessages />
            </RequireClient>
          }
        />
        <Route
          path="/client/messages/archive"
          element={
            <RequireClient>
              <ClientMessages />
            </RequireClient>
          }
        />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email/pending" element={<VerifyEmailPending />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/onboarding/rotate-password" element={<RotatePassword />} />
        <Route path="/onboarding/subscribe" element={<SubscribeActivate />} />
        <Route path="/onboarding/professional" element={<ProfessionalSetup />} />

        {/* Buyer flow — isolated client platform */}
        <Route path="/buyer/dashboard" element={<Navigate to="/start" replace />} />
        <Route
          path="/start"
          element={
            <RequireRoleDesk desk="buyer">
              <BuyerStart />
            </RequireRoleDesk>
          }
        />
        <Route
          path="/location"
          element={
            <RequireBuyer>
              <BuyerLocation />
            </RequireBuyer>
          }
        />
        <Route
          path="/home-type"
          element={
            <RequireBuyer>
              <BuyerHomeType />
            </RequireBuyer>
          }
        />
        <Route
          path="/lot"
          element={
            <RequireBuyer>
              <BuyerLot />
            </RequireBuyer>
          }
        />
        <Route
          path="/exterior"
          element={
            <RequireBuyer>
              <BuyerExterior />
            </RequireBuyer>
          }
        />
        <Route
          path="/interior"
          element={
            <RequireBuyer>
              <BuyerInterior />
            </RequireBuyer>
          }
        />
        <Route
          path="/lifestyle"
          element={
            <RequireBuyer>
              <BuyerLifestyle />
            </RequireBuyer>
          }
        />
        <Route
          path="/accessibility"
          element={
            <RequireBuyer>
              <BuyerAccessibility />
            </RequireBuyer>
          }
        />
        <Route
          path="/notes"
          element={
            <RequireBuyer>
              <Navigate to="/accessibility?panel=notes" replace />
            </RequireBuyer>
          }
        />
        <Route
          path="/tips"
          element={
            <RequireBuyer>
              <Navigate to="/accessibility?panel=tips" replace />
            </RequireBuyer>
          }
        />
        <Route
          path="/transaction"
          element={
            <RequireBuyer>
              <Navigate to="/accessibility?panel=transaction" replace />
            </RequireBuyer>
          }
        />
        <Route
          path="/complete"
          element={
            <RequireBuyer>
              <BuyerComplete />
            </RequireBuyer>
          }
        />

        {/* Agent area */}
        <Route path="/agent/login" element={<AgentLogin />} />
        <Route
          path="/agent/dashboard"
          element={
            <RequireAgent>
              <AgentDashboard />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/start-here"
          element={
            <RequireAgent>
              <StartHereGuide />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/prospects"
          element={
            <RequireAgent>
              <ProspectsPage />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/prospects/:id"
          element={
            <RequireAgent>
              <ProspectsPage />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/clients"
          element={
            <RequireAgent>
              <AgentClients />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/client/:id"
          element={
            <RequireAgent>
              <AgentClientDetail />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/files"
          element={
            <RequireAgent>
              <AgentFiles />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/files/:id"
          element={
            <RequireAgent>
              <AgentFileDetail />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/documents"
          element={
            <RequireAgent>
              <AgentDocuments />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/calendar"
          element={
            <RequireAgent>
              <AgentCalendar />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/hub"
          element={
            <RequireAgent>
              <Navigate to="/agent/hub/hall" replace />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/hub/:category"
          element={
            <RequireAgent>
              <TheHubPage />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/messages"
          element={
            <RequireAgent>
              <AgentMessages />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/messages/archive"
          element={
            <RequireAgent>
              <AgentMessages />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/office"
          element={
            <RequireAgent>
              <AgentMessages />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/office/archive"
          element={
            <RequireAgent>
              <AgentMessages />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/audit"
          element={
            <RequireAgent>
              <AgentAudit />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/settings"
          element={<FilmNavigate to="/account" />}
        />
        <Route
          path="/agent/responses"
          element={
            <RequireAgent>
              <AgentResponseHub />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/new"
          element={
            <RequireAgent>
              <AgentResponseNew />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/location"
          element={
            <RequireAgent>
              <AgentResponseLocation />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/exterior"
          element={
            <RequireAgent>
              <AgentResponseExterior />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/interior"
          element={
            <RequireAgent>
              <AgentResponseInterior />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/systems"
          element={
            <RequireAgent>
              <AgentResponseSystems />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/condition"
          element={
            <RequireAgent>
              <AgentResponseCondition />
            </RequireAgent>
          }
        />
        <Route
          path="/agent/responses/:id/summary"
          element={
            <RequireAgent>
              <AgentResponseSummary />
            </RequireAgent>
          }
        />

        {/* Seller flow — isolated from buyer routes and buyer state */}
        <Route path="/seller/dashboard" element={<Navigate to="/seller/home" replace />} />
        <Route element={<RequireRoleDesk desk="seller" />}>
          <Route path="/seller/home" element={<SellerHome />} />
          <Route path="/seller/welcome" element={<SellerWelcome />} />
          <Route path="/seller/property-details" element={<SellerPropertyDetails />} />
          <Route path="/seller/interior-features" element={<SellerInteriorFeatures />} />
          <Route path="/seller/exterior-features" element={<SellerExteriorFeatures />} />
          <Route path="/seller/upgrades" element={<SellerUpgrades />} />
          <Route path="/seller/condition-notes" element={<SellerConditionNotes />} />
          <Route path="/seller/photos" element={<SellerPhotos />} />
          <Route path="/seller/improvements" element={<SellerImprovements />} />
          <Route
            path="/seller/listing-readiness"
            element={<Navigate to="/seller/improvements?panel=readiness" replace />}
          />
          <Route path="/seller/tips" element={<Navigate to="/seller/improvements?panel=tips" replace />} />
          <Route path="/seller/notes" element={<Navigate to="/seller/welcome?panel=notes" replace />} />
          <Route path="/seller/portfolio-summary" element={<SellerPortfolioSummary />} />
        </Route>
        <Route path="/seller/:clientCode" element={<ClientPortalPage kind="seller" />} />

        {/* Install instructions */}
        <Route path="/install" element={<InstallPage />} />
        <Route path="/manual" element={<ManualRedirect />} />
        <Route path="/manual/:role" element={<ManualRedirect />} />
        <Route path="/manual/:role/:slug" element={<ManualRedirect />} />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </OnboardingGate>
    </BrowserRouter>
  );
}
